import { PureComponent, ReactNode } from 'react';
import PadV2PxElementCard from './padV2/PadV2PxElementCard';
import { Table } from 'antd';
import { EditableCellV2 } from './padV2/MedTableRenderer';
import AntAutoCompleteV2 from './padV2/AntAutoComplete';
import Column from 'antd/lib/table/Column';
import { connect } from 'react-redux';
import { getElementsFromPrescription } from '../../../../store/selectors/prescriptionPad';
import { GlobalState } from '../../../../store/reducers';
import { headerStyle } from '../../../common/TableUtils';
import { EkaFocosableElement } from '../../../common/types/EkaFocusable';
import { PAD_V2_CONTAINER_SELECTOR } from './PadContainer';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../store/actions';
import { upsertElement } from '../../../../store/actions/prescriptionPad';
import {
    eyeNameMap,
    getOpPropertyPlaceholder,
    getUITable,
    onOpPropertySearch,
    propertyMap,
} from '../../../../pages/opUtils';
import { PX_OP_KEYS } from '../../../../store/types/prescription';

interface CompProps {
    pxKey: PX_OP_KEYS;
}

interface Props<T> extends CompProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: (T & { id: string })[];
    columns: { key: keyof Omit<T, 'eye'>; title: string }[];
    icon: JSX.Element;
    heading: string;
    renderHeaderRight?: React.FC;
}

interface State<T> {
    elements: (T & { id: string })[];
}

export type OptionTypeBase = {
    [k: string]: { value?: string; id: string; custom?: string } | undefined;
    eye: { value: string; id: string };
};

class OpthalTable<T extends OptionTypeBase> extends PureComponent<Props<T>, State<T>> {
    REF_MAP: EkaFocosableElement[][] = [];

    state = {
        elements: getUITable<T & { id: string }>(this.props.elements, this.props.pxKey),
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!this.REF_MAP[elIndex]) {
            this.REF_MAP[elIndex] = [];
        }

        this.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    onPropertyChange = (
        index: number,
        columnIndex: number,
        key: keyof Omit<T, 'eye'>,
        option?: { value: string; id: string; custom: string },
    ) => {
        const { pxKey, columns } = this.props;
        const newItem: T & { id: string } = {
            ...this.state.elements[index],
            [key]: {
                ...option,
                custom: option?.custom || option?.value,
                ...propertyMap[pxKey]?.[this.state.elements[index].id]?.[key as string],
            },
        };

        this.props.dispatch(upsertElement(pxKey, newItem));
        if (option) {
            const nextRowIndex = index + Math.floor(columnIndex / (columns.length - 1));
            const nextColumn = (columnIndex + 1) % columns.length;

            // Only focus if value column is empty

            if (!this.state.elements[nextRowIndex]?.[columns[nextColumn]?.key]?.value) {
                this.REF_MAP[nextRowIndex]?.[nextColumn]?.focus();
            }
        }
    };

    // Static members cannot reference class type parameters.
    static getDerivedStateFromProps(props: Props<any>): Partial<State<any>> | null {
        return {
            elements: getUITable<any>(props.elements, props.pxKey),
        };
    }

    render(): ReactNode {
        const { elements } = this.state;
        const { columns, icon, heading, renderHeaderRight } = this.props;

        return (
            <>
                <PadV2PxElementCard
                    icon={icon}
                    headingText={heading}
                    iconContainerBgClassName="bg-accent-dark-green"
                    bodyCssClassName="space-y-8 min-h-172 rounded-8"
                    renderHeaderRight={renderHeaderRight}
                >
                    <Table
                        dataSource={elements}
                        pagination={false}
                        tableLayout="fixed"
                        bordered
                        rowClassName={(record, index) => `editable-row`}
                    >
                        <Column
                            title="Eye"
                            dataIndex="eye"
                            key="eye"
                            width={'15%'}
                            render={(text?: { value: 'RE' | 'LE' }) => (
                                <span className="space-x-8">
                                    <span>{text?.value} </span>
                                    {text?.value && (
                                        <span className="text-darwin-neutral-400">
                                            {eyeNameMap[text?.value]}
                                        </span>
                                    )}
                                </span>
                            )}
                            onHeaderCell={headerStyle}
                        />
                        {columns.map((column, columnIndex) => (
                            <Column
                                title={column.title}
                                onHeaderCell={headerStyle}
                                render={(
                                    obj: { value: string } | null | undefined,
                                    _: { value: string },
                                    index: number,
                                ) => (
                                    <>
                                        <EditableCellV2
                                            defaultValue={elements[index][column.key]?.custom}
                                            setRef={(ref) => {
                                                this.setPropertyInputRef(ref, index, columnIndex);
                                                return;
                                            }}
                                            label={getOpPropertyPlaceholder<T>(column.key)}
                                        >
                                            <AntAutoCompleteV2
                                                autoFocus
                                                onSearch={onOpPropertySearch<T>(column.key)}
                                                selectedValue={
                                                    elements[index][column.key]?.value
                                                        ? (elements[index][column.key] as {
                                                              id: string;
                                                              value: string;
                                                              custom: string;
                                                          })
                                                        : undefined
                                                }
                                                debounceDelay={200}
                                                getLabelToCompare={(o: {
                                                    id: string;
                                                    value: string;
                                                    custom: string;
                                                }) => o.custom}
                                                placeholder={getOpPropertyPlaceholder<T>(
                                                    column.key,
                                                )}
                                                customDropdownWidth={180}
                                                onSelect={(option) => {
                                                    this.onPropertyChange(
                                                        index,
                                                        columnIndex,
                                                        column.key,
                                                        option,
                                                    );
                                                }}
                                                containerSelectorToAttachDropdown={
                                                    PAD_V2_CONTAINER_SELECTOR
                                                }
                                                key={`${index}-${columnIndex}`}
                                            />
                                        </EditableCellV2>
                                    </>
                                )}
                            />
                        ))}
                    </Table>
                </PadV2PxElementCard>
            </>
        );
    }
}

export default connect((state: GlobalState, props: CompProps) => ({
    elements: getElementsFromPrescription(props.pxKey)(state),
}))(OpthalTable);
